/**
 * Family & marriage Q&A corpus — deterministic generator (no AI credits).
 * Self-contained answers, no prices, no guaranteed outcomes.
 */
import { FAM_REVIEWED_TH, FAM_TOPICS, type FamTopic } from "./index";
import { FAM_CITIES, FAM_INTENTS, FAM_NATIONALITIES } from "./keywords";

import { expandWave } from "../_qa/wave-expander";

export type FamQA = { slug: string; q: string; a: string; synonyms: string[]; topicSlug?: string };

const ASOF = `ข้อมูล ณ ${FAM_REVIEWED_TH} · ค่าบริการแจ้งเป็นรายเคส สอบถามทางโทรศัพท์ LINE หรืออีเมล`;

const CORE: FamQA[] = [
  {
    slug: "marriage-valid-when",
    q: "การสมรสในไทยสมบูรณ์เมื่อไหร่",
    a: `การสมรสสมบูรณ์เมื่อได้จดทะเบียนแล้วตามประมวลกฎหมายแพ่งและพาณิชย์ มาตรา 1457 การจัดพิธีหรือการอยู่กินฉันสามีภริยาโดยไม่จดทะเบียนไม่ก่อให้เกิดการสมรสตามกฎหมายไทย และคู่สมรสต้องยินยอมโดยเปิดเผยต่อหน้านายทะเบียนตามมาตรา 1458 ${ASOF}`,
    synonyms: ["จดทะเบียนสมรสถึงจะถูกกฎหมายไหม", "แต่งงานไม่จดทะเบียนมีผลไหม"],
    topicSlug: "marriage-registration-thailand",
  },
  {
    slug: "marriage-equality-date",
    q: "สมรสเท่าเทียมในไทยมีผลใช้บังคับตั้งแต่เมื่อไหร่",
    a: `พระราชบัญญัติแก้ไขเพิ่มเติมประมวลกฎหมายแพ่งและพาณิชย์ (ฉบับที่ 24) พ.ศ. 2567 มีผลใช้บังคับวันที่ 23 มกราคม 2568 ทำให้บุคคลสองคนจดทะเบียนสมรสกันได้โดยไม่จำกัดเพศ เอกสารและขั้นตอนที่สำนักทะเบียนใช้เป็นแนวเดียวกับการจดทะเบียนสมรสทั่วไป ${ASOF}`,
    synonyms: ["same-sex marriage thailand effective date", "จดทะเบียนสมรสเพศเดียวกันได้ไหม"],
    topicSlug: "marriage-registration-thailand",
  },
  {
    slug: "min-age-marriage",
    q: "อายุขั้นต่ำในการจดทะเบียนสมรสของไทยคือเท่าไหร่",
    a: `ประมวลกฎหมายแพ่งและพาณิชย์ มาตรา 1448 กำหนดว่าการสมรสจะทำได้เมื่อบุคคลทั้งสองมีอายุ 18 ปีบริบูรณ์แล้ว เว้นแต่ศาลอนุญาตให้ทำการสมรสก่อนนั้นได้เมื่อมีเหตุอันสมควร นายทะเบียนจะตรวจอายุจากเอกสารแสดงตนก่อนรับจดทะเบียนทุกครั้ง ${ASOF}`,
    synonyms: ["อายุ 18 จดทะเบียนสมรสได้ไหม", "legal age of marriage thailand"],
    topicSlug: "marriage-registration-thailand",
  },
  {
    slug: "prenup-after-marriage",
    q: "ทำสัญญาก่อนสมรสหลังจดทะเบียนแล้วได้หรือไม่",
    a: `ไม่ได้ในฐานะสัญญาก่อนสมรส เพราะประมวลกฎหมายแพ่งและพาณิชย์ มาตรา 1466 กำหนดให้จดแจ้งข้อความไว้ในทะเบียนสมรสพร้อมกับการจดทะเบียนสมรส หรือทำเป็นหนังสือลงลายมือชื่อคู่สมรสและพยานสองคนแนบท้ายทะเบียนสมรสและจดแจ้งไว้ หากทำภายหลังต้องพิจารณาเป็นข้อตกลงรูปแบบอื่นซึ่งผลทางกฎหมายต่างกัน ${ASOF}`,
    synonyms: ["prenup ทำหลังแต่งได้ไหม", "prenuptial agreement thailand timing"],
    topicSlug: "prenuptial-agreement",
  },
  {
    slug: "divorce-two-routes",
    q: "การหย่าในไทยทำได้กี่ทาง",
    a: `ประมวลกฎหมายแพ่งและพาณิชย์ มาตรา 1514 กำหนดสองทาง คือการหย่าโดยความยินยอมซึ่งต้องทำเป็นหนังสือมีพยานลงลายมือชื่ออย่างน้อยสองคนและไปจดทะเบียนหย่าที่สำนักทะเบียน และการหย่าโดยคำพิพากษาของศาลเมื่อมีเหตุฟ้องหย่าตามกฎหมาย ระยะเวลาของสองช่องทางต่างกันมาก ${ASOF}`,
    synonyms: ["หย่าต้องขึ้นศาลไหม", "divorce thailand consent vs court"],
    topicSlug: "divorce-registration",
  },
  {
    slug: "kr22-what-is",
    q: "ทะเบียนฐานะแห่งครอบครัว (คร.22) คืออะไร",
    a: `คือการบันทึกสถานะครอบครัวไว้ในทะเบียนของไทยตามพระราชบัญญัติจดทะเบียนครอบครัว พ.ศ. 2478 ใช้กับกรณีที่จดทะเบียนสมรสตามกฎหมายต่างประเทศแล้วต้องการให้ทะเบียนไทยแสดงสถานะสมรส ต้องรับรองเอกสารต้นทาง แปลเป็นภาษาไทย และผ่านนิติกรณ์กรมการกงสุลก่อนยื่นที่สำนักทะเบียน ไม่ใช่การจดทะเบียนสมรสใหม่ ${ASOF}`,
    synonyms: ["บันทึกฐานะแห่งครอบครัวคืออะไร", "kr22 family status registration thailand"],
    topicSlug: "marriage-abroad-recognition",
  },
  {
    slug: "single-status-validity",
    q: "หนังสือรับรองสถานะโสดใช้ได้นานเท่าไหร่",
    a: `กฎหมายไม่ได้กำหนดอายุกลางไว้ หน่วยงานผู้รับเป็นผู้กำหนดเอง สำนักทะเบียนและสถานทูตหลายแห่งกำหนดเป็นจำนวนเดือนนับจากวันออกเอกสาร แนวปฏิบัติที่ปลอดภัยคือโทรยืนยันกับหน่วยงานที่จะยื่นก่อนเริ่มขอเอกสาร แล้วจัดคิวสถานทูตกับคิวนิติกรณ์ให้ต่อเนื่อง ${ASOF}`,
    synonyms: ["affidavit of single status อายุกี่เดือน", "single status certificate validity thailand"],
    topicSlug: "affirmation-freedom-to-marry",
  },
  {
    slug: "apostille-marriage-thailand",
    q: "ทะเบียนสมรสไทยใช้ Apostille ได้แล้วหรือยัง",
    a: `ยังไม่ได้ในขณะนี้ ประเทศไทยเข้าเป็นภาคีอนุสัญญากรุงเฮกว่าด้วยการยกเลิกการรับรองเอกสารมหาชนต่างประเทศ โดยจะมีผลใช้บังคับกับไทยวันที่ 28 กุมภาพันธ์ 2570 ระหว่างนี้ให้ตรวจเส้นทางแปลและรับรองกับหน่วยงานผู้รับ ซึ่งอาจต้องใช้นิติกรณ์กรมการกงสุลและสถานทูตตามข้อกำหนดของแต่ละกรณี ${ASOF}`,
    synonyms: ["apostille ไทยเริ่มเมื่อไหร่", "thailand apostille marriage certificate"],
    topicSlug: "marriage-documents-overseas-use",
  },
  {
    slug: "consular-fee-marriage-doc",
    q: "ค่าธรรมเนียมนิติกรณ์รับรองคำแปลทะเบียนสมรสเท่าไหร่",
    a: `กรมการกงสุล กระทรวงการต่างประเทศ ประกาศอัตราค่าธรรมเนียมนิติกรณ์แบบธรรมดา 200 บาทต่อตราประทับ และแบบด่วน 400 บาทต่อตราประทับ (consular.mfa.go.th) ซึ่งเป็นค่าธรรมเนียมราชการแยกจากค่าบริการดำเนินการ ควรตรวจอัตราปัจจุบันกับกรมการกงสุลก่อนยื่นทุกครั้ง ${ASOF}`,
    synonyms: ["ค่าธรรมเนียมกรมการกงสุล 200 บาท", "mfa legalization fee thailand"],
    topicSlug: "marriage-documents-overseas-use",
  },
  {
    slug: "interpreter-required-district",
    q: "ต้องมีล่ามในวันจดทะเบียนสมรสหรือไม่",
    a: `ขึ้นกับดุลพินิจของนายทะเบียน หลักคือความยินยอมต้องเกิดจากความเข้าใจตามประมวลกฎหมายแพ่งและพาณิชย์ มาตรา 1458 หากคู่สมรสฝ่ายหนึ่งไม่เข้าใจภาษาไทย สำนักทะเบียนหลายแห่งกำหนดให้มีล่ามไปด้วย เงื่อนไขเรื่องเอกสารและคุณสมบัติล่ามต่างกันในแต่ละสำนักทะเบียน ควรยืนยันก่อนวันนัด ${ASOF}`,
    synonyms: ["ล่ามอำเภอจดทะเบียนสมรส", "interpreter marriage registration thailand"],
    topicSlug: "district-interpreter",
  },
];

function topicQA(t: FamTopic): FamQA[] {
  const out: FamQA[] = [
    {
      slug: `${t.slug}-overview`,
      q: `${t.th} ทำอย่างไร`,
      a: `${t.summaryTh} หน่วยงานผู้รับหลักคือ ${t.authorityTh} ${ASOF}`,
      synonyms: [`${t.th} คืออะไร`, `${t.en.toLowerCase()} thailand`, `ขั้นตอน${t.th}`],
      topicSlug: t.slug,
    },
    {
      slug: `${t.slug}-documents`,
      q: `${t.th} ต้องเตรียมเอกสารอะไรบ้าง`,
      a: `เอกสารที่มักต้องใช้: ${t.requirementsTh.join(" · ")} รายการอาจต่างกันตามสำนักทะเบียนหรือสถานทูตที่ยื่น ควรยืนยันกับหน่วยงานผู้รับก่อนวันนัด ${ASOF}`,
      synonyms: [`${t.th} เอกสารที่ต้องใช้`, `เตรียมเอกสาร${t.th}`],
      topicSlug: t.slug,
    },
    {
      slug: `${t.slug}-steps`,
      q: `ขั้นตอน${t.th} มีกี่ขั้น`,
      a: `${t.stepsTh.map((s, i) => `${i + 1}) ${s.title} — ${s.detail}`).join(" ")} ${ASOF}`,
      synonyms: [`${t.th} ขั้นตอนละเอียด`, `${t.en.toLowerCase()} process`],
      topicSlug: t.slug,
    },
    {
      slug: `${t.slug}-channels`,
      q: `${t.th} ยื่นได้ที่ช่องทางใด`,
      a: `ช่องทางที่หน่วยงานเปิดให้บริการ: ${t.channelsTh.join(" · ")} ช่องทางออนไลน์และการมอบอำนาจอาจไม่เปิดทุกแห่ง ควรตรวจกับหน่วยงานพื้นที่ที่จะยื่นก่อน ${ASOF}`,
      synonyms: [`${t.th} ยื่นที่ไหน`, `${t.th} ทำออนไลน์ได้ไหม`],
      topicSlug: t.slug,
    },
    {
      slug: `${t.slug}-facts`,
      q: `${t.th} มีข้อเท็จจริงสำคัญอะไรที่ต้องรู้`,
      a: `${t.factsTh.map((f) => `${f.label}: ${f.value}`).join(" · ")} ${t.citationTh} ${ASOF}`,
      synonyms: [`${t.th} สรุปข้อมูลสำคัญ`, `${t.th} หน่วยงานไหนรับผิดชอบ`],
      topicSlug: t.slug,
    },
    {
      slug: `${t.slug}-tips`,
      q: `มีคำแนะนำอะไรก่อนดำเนินการ${t.th}`,
      a: `คำแนะนำก่อนยื่น: ${t.tipsTh.slice(0, 6).join(" · ")} ${ASOF}`,
      synonyms: [`เตรียมตัว${t.th}`, `${t.th} ทำอย่างไรให้เรียบร้อย`],
      topicSlug: t.slug,
    },
    {
      slug: `${t.slug}-warnings`,
      q: `${t.th} มีข้อควรระวังหรือสาเหตุถูกตีกลับอะไรบ้าง`,
      a: `ข้อควรระวังที่พบบ่อย: ${t.warningsTh.slice(0, 6).join(" · ")} ${t.citationTh} ${ASOF}`,
      synonyms: [`${t.th} ถูกตีกลับเพราะอะไร`, `${t.th} ผิดพลาดบ่อย`],
      topicSlug: t.slug,
    },
  ];

  for (const c of FAM_CITIES) {
    out.push({
      slug: `${t.slug}-city-${c.slug}`,
      q: `${t.th} ที่${c.th} ดำเนินการอย่างไร`,
      a: `งาน${t.th} ในพื้นที่${c.th} ยื่นต่อสำนักทะเบียนหรือหน่วยงานที่มีเขตอำนาจครอบคลุมภูมิลำเนาหรือที่พักอาศัย แต่ละแห่งกำหนดวิธีนัดหมาย รอบเวลารับคำขอ และรายการเอกสารเพิ่มเติมได้เอง จึงควรยืนยันกับหน่วยงานพื้นที่ก่อนเดินทางไปยื่น เราช่วยตรวจเอกสาร จัดล่าม และเดินเรื่องให้ได้ ${ASOF}`,
      synonyms: [`${t.th} ${c.th}`, `${t.en.toLowerCase()} ${c.slug}`],
      topicSlug: t.slug,
    });
  }

  for (const n of FAM_NATIONALITIES) {
    out.push({
      slug: `${t.slug}-nat-${n.slug}`,
      q: `คู่สมรสสัญชาติ${n.th} ดำเนินการเรื่อง${t.th} ต่างจากสัญชาติอื่นไหม`,
      a: `หลักเกณฑ์ตามกฎหมายไทยใช้เป็นการทั่วไปไม่ได้แยกตามสัญชาติ สิ่งที่ต่างกันในทางปฏิบัติคือรูปแบบเอกสารที่สถานทูต${n.th} (${n.en}) ออกให้ และเส้นทางการรับรองเอกสารต้นทางก่อนนำมาใช้ในไทย จึงควรตรวจกับสถานทูตของคู่สมรสก่อนว่าออกหนังสือรับรองแบบใดและต้องนัดหมายล่วงหน้ากี่วัน ${ASOF}`,
      synonyms: [`${t.th} คน${n.th}`, `${t.en.toLowerCase()} ${n.en.toLowerCase()}`],
      topicSlug: t.slug,
    });
  }

  return out;
}

function intentQA(): FamQA[] {
  return FAM_INTENTS.map((i) => ({
    slug: `intent-${i.slug}`,
    q: `กรณี${i.th} ต้องดำเนินการอะไรกับหน่วยงานราชการ`,
    a: `สำหรับกรณี${i.th} ให้ไล่ตรวจสามชั้น คือ (1) สถานะตามกฎหมายครอบครัวไทยตามประมวลกฎหมายแพ่งและพาณิชย์ บรรพ 5 (2) เอกสารที่หน่วยงานผู้รับต้องการและรูปแบบคำแปลที่ยอมรับ และ (3) เส้นทางการรับรองเอกสาร ทั้งนิติกรณ์กรมการกงสุลและการรับรองของสถานทูตที่เกี่ยวข้อง รายการที่แต่ละสำนักงานเรียกต่างกัน จึงควรยืนยันก่อนยื่น ${ASOF}`,
    synonyms: [`${i.th} ต้องทำอะไรบ้าง`, `${i.th} เอกสารที่ต้องใช้`],
  }));
}

function cityQA(): FamQA[] {
  return FAM_CITIES.map((c) => ({
    slug: `city-${c.slug}`,
    q: `ที่${c.th} จดทะเบียนสมรสหรือจดทะเบียนหย่าได้ที่ไหน`,
    a: `ในพื้นที่${c.th} งานทะเบียนครอบครัวยื่นต่อสำนักงานเขตหรือที่ว่าการอำเภอที่มีเขตอำนาจ ส่วนคำแปลเอกสารต่างประเทศต้องผ่านนิติกรณ์กรมการกงสุลก่อนตามที่นายทะเบียนกำหนด แต่ละแห่งกำหนดรอบเวลาและช่องทางนัดหมายเอง ควรยืนยันก่อนเดินทางไปยื่น และเตรียมล่ามหากคู่สมรสไม่เข้าใจภาษาไทย ${ASOF}`,
    synonyms: [`จดทะเบียนสมรส ${c.th}`, `จดทะเบียนหย่า ${c.th}`, `marriage registration ${c.slug}`],
  }));
}

function buildAll(): FamQA[] {
  const WAVE = expandWave({ prefix: "fam", asOf: ASOF, authorityTh: "สำนักทะเบียนหรือหน่วยงานผู้รับเอกสาร", clusterTh: "งานทะเบียนครอบครัว", primary: [...FAM_TOPICS, ...FAM_NATIONALITIES], secondary: FAM_CITIES, intents: FAM_INTENTS });

const out: FamQA[] = [...WAVE, ...CORE];
  for (const t of FAM_TOPICS) out.push(...topicQA(t));
  out.push(...intentQA(), ...cityQA());
  const seen = new Set<string>();
  return out.filter((q) => (seen.has(q.slug) ? false : (seen.add(q.slug), true)));
}

export const FAM_QA_ALL: FamQA[] = buildAll();
export const FAM_QA_BY_SLUG: Record<string, FamQA> = Object.fromEntries(
  FAM_QA_ALL.map((q) => [q.slug, q]),
);
export const FAM_QA_COUNT = FAM_QA_ALL.length;
